/**
 * CAOS-225 — the draft text for the two legal pages the sign-in footer links to.
 *
 * ORIGINAL WORDING ONLY. Salesforce's published legal pages were read as a coverage benchmark
 * (which topics a comparable SaaS platform's Terms/Privacy cover) and nothing here is copied from
 * them — every sentence below was written fresh for this platform and stripped of anything that
 * only applies to an enterprise vendor with a sales-negotiated MSA (no named support tiers, no
 * data-processing-addendum cross-references, no multi-entity affiliate language).
 *
 * `[Legal Entity Name]` is a deliberate placeholder, not a guess dressed up as a fact: no
 * incorporated entity name, formation state, or registered address exists anywhere in this
 * repository or its docs, and inventing one would put a false legal claim in a page meant to
 * protect the company. Whoever fills it in should do so alongside the attorney review both pages
 * already call for below.
 *
 * Bare-bones by design (the ticket's own instruction): enough to be a real first Terms of Service
 * and Privacy Policy for a small US company running a cloud platform, not an exhaustive enterprise
 * contract. Expand later as real obligations appear (a DPA, a support SLA, a sub-processor list).
 */

export interface LegalBlock {
  readonly heading: string;
  /** A plain string renders as a paragraph; a string array renders as a bullet list. */
  readonly body: ReadonlyArray<string | readonly string[]>;
}

export interface LegalDocument {
  readonly title: string;
  readonly effectiveDateNote: string;
  readonly draftNotice: string;
  readonly blocks: readonly LegalBlock[];
}

const DRAFT_NOTICE =
  'This page is a working draft prepared ahead of public launch. It has not yet had attorney ' +
  'review, and the bracketed placeholders below (the legal entity name and contact address) are ' +
  'not yet confirmed. It is not legal advice, and it is not final.';

const ENTITY = '[Legal Entity Name]';

export const TERMS_OF_SERVICE: LegalDocument = {
  title: 'Terms of Service',
  effectiveDateNote: 'Draft — effective date to be set at publication.',
  draftNotice: DRAFT_NOTICE,
  blocks: [
    {
      heading: '1. Agreement to these Terms',
      body: [
        `These Terms of Service ("Terms") are an agreement between you, or the organisation you ` +
          `represent ("you"), and ${ENTITY} ("we," "us," or "our"), and govern your access to and ` +
          `use of Cloud Atlantis OS and any associated services (the "Service"). By creating an ` +
          `account or otherwise using the Service, you agree to these Terms. If you are accepting ` +
          `on behalf of an organisation, you confirm you have authority to bind it.`,
      ],
    },
    {
      heading: '2. The Service',
      body: [
        `The Service lets you configure, build on, and run a metadata-defined workspace: ` +
          `objects, pages, apps, automations, and the data you put into them. We may add, change, ` +
          `or remove features over time, and we will try to give reasonable notice before removing ` +
          `one you rely on.`,
      ],
    },
    {
      heading: '3. Accounts and Registration',
      body: [
        `You need an account to use the Service. You are responsible for the accuracy of the ` +
          `information you give us, for keeping your credentials confidential, and for everything ` +
          `that happens under your account, whether or not you authorised it, once you know about it ` +
          `and have had a reasonable chance to act. Tell us promptly if you suspect unauthorised use.`,
      ],
    },
    {
      heading: '4. Acceptable Use',
      body: [
        'You agree not to use the Service to:',
        [
          'break the law, or infringe someone else’s rights;',
          'upload malicious code or attempt to disrupt or gain unauthorised access to the Service or ' +
            'any other account;',
          'reverse engineer the Service beyond what applicable law allows;',
          'resell or provide the Service to a third party as your own offering without our written ' +
            'agreement;',
          'overload or abuse shared infrastructure in a way that degrades the Service for others.',
        ],
        'We may suspend access that we reasonably believe violates this section while we investigate.',
      ],
    },
    {
      heading: '5. Your Content and Data',
      body: [
        `You own the data and configuration you put into the Service ("Your Content"). You grant ` +
          `us the limited right to host, process, and transmit Your Content solely to provide the ` +
          `Service to you. We do not claim ownership of Your Content and will not use it to train ` +
          `generally-available models or sell it to third parties. See our Privacy Policy for how we ` +
          `handle personal data specifically.`,
      ],
    },
    {
      heading: '6. Our Intellectual Property',
      body: [
        `The Service itself — its software, design, and branding — is owned by ${ENTITY} ` +
          `or our licensors. These Terms give you the right to use the Service; they do not transfer ` +
          `any ownership of it to you.`,
      ],
    },
    {
      heading: '7. Fees',
      body: [
        `Some use of the Service may require payment, at the price and interval shown to you at ` +
          `sign-up or in your order. Fees are non-refundable except where this section or the law ` +
          `says otherwise. We will give notice before a price change takes effect on your next ` +
          `billing cycle.`,
      ],
    },
    {
      heading: '8. Disclaimers',
      body: [
        `THE SERVICE IS PROVIDED "AS IS." TO THE EXTENT THE LAW ALLOWS, WE DISCLAIM ALL IMPLIED ` +
          `WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND ` +
          `NON-INFRINGEMENT. We do not promise the Service will be uninterrupted or error-free.`,
      ],
    },
    {
      heading: '9. Limitation of Liability',
      body: [
        `TO THE EXTENT THE LAW ALLOWS, NEITHER PARTY IS LIABLE FOR INDIRECT, INCIDENTAL, OR ` +
          `CONSEQUENTIAL DAMAGES, AND OUR TOTAL LIABILITY UNDER THESE TERMS WILL NOT EXCEED THE ` +
          `AMOUNT YOU PAID US FOR THE SERVICE IN THE TWELVE MONTHS BEFORE THE CLAIM AROSE. Nothing ` +
          `here limits liability that cannot be limited by law.`,
      ],
    },
    {
      heading: '10. Term, Suspension, and Termination',
      body: [
        `These Terms apply for as long as you use the Service. You may stop using the Service at ` +
          `any time. We may suspend or terminate access for a material breach of these Terms that ` +
          `you do not cure after notice, or immediately where needed to protect the Service or other ` +
          `users. We will give you a reasonable opportunity to export Your Content on termination, ` +
          `except where that is not possible for legal or security reasons.`,
      ],
    },
    {
      heading: '11. Governing Law',
      body: [
        `These Terms are governed by the laws of the United States and the state in which ${ENTITY} ` +
          `is formed, without regard to conflict-of-law rules. [State/venue for disputes to be ` +
          `confirmed with the entity's registered state.]`,
      ],
    },
    {
      heading: '12. Changes to These Terms',
      body: [
        `We may update these Terms as the Service changes. We will post the updated Terms here and ` +
          `update the effective date; continued use after a change takes effect means you accept the ` +
          `update. For material changes we will make a reasonable effort to let you know directly.`,
      ],
    },
    {
      heading: '13. Contact',
      body: [`Questions about these Terms can be sent to [contact address to be confirmed].`],
    },
  ],
};

export const PRIVACY_POLICY: LegalDocument = {
  title: 'Privacy Policy',
  effectiveDateNote: 'Draft — effective date to be set at publication.',
  draftNotice: DRAFT_NOTICE,
  blocks: [
    {
      heading: '1. Overview',
      body: [
        `This Privacy Policy explains what personal data ${ENTITY} ("we," "us," or "our") collects ` +
          `through Cloud Atlantis OS (the "Service"), why, and what choices you have about it. It ` +
          `covers the people who sign up for and use the Service directly; it does not cover the ` +
          `separate choices an organisation using the Service makes about the data it puts into its ` +
          `own workspace, which that organisation controls.`,
      ],
    },
    {
      heading: '2. Information We Collect',
      body: [
        'We collect:',
        [
          'Account information — name, work email, and password (stored as a salted hash, never ' +
            'in plain text) when you sign up;',
          'Usage information — the actions you take in the Service and basic technical details ' +
            '(browser, IP address, timestamps) needed to operate and secure it;',
          'Support and contact information — anything you send us when you ask for help or give ' +
            'feedback;',
          'The content your organisation configures or stores in its own workspace, which we process ' +
            'as described in Section 3, on that organisation’s behalf.',
        ],
      ],
    },
    {
      heading: '3. How We Use Information',
      body: [
        'We use this information to:',
        [
          'provide, maintain, and secure the Service;',
          'authenticate you and keep your account safe;',
          'respond to support requests and communicate about the Service;',
          'understand how the Service is used so we can improve it;',
          'meet legal obligations.',
        ],
        'We do not sell personal data, and we do not use it to serve third-party advertising.',
      ],
    },
    {
      heading: '4. How We Share Information',
      body: [
        'We share information only with:',
        [
          'infrastructure providers that host or process it on our behalf, under a confidentiality ' +
            'obligation, and only to the extent needed to run the Service;',
          'a successor entity in the event of a merger, acquisition, or sale of assets, who would be ' +
            'bound by this Policy or a materially equivalent one;',
          'law enforcement or another party where required by law, or to protect the rights, safety, ' +
            'or property of our users or the public.',
        ],
      ],
    },
    {
      heading: '5. Data Security',
      body: [
        `We use encryption in transit and at rest, access controls, and routine monitoring to protect ` +
          `your information. No system is perfectly secure, and we cannot guarantee against every ` +
          `possible breach, but we will notify affected customers without undue delay if one occurs ` +
          `and affects their data, as required by law.`,
      ],
    },
    {
      heading: '6. Data Retention',
      body: [
        `We keep account and usage information for as long as your account is active, and for a ` +
          `reasonable period after closure to meet legal, accounting, or dispute-resolution needs. ` +
          `You can ask us to delete your account information sooner, subject to Section 7.`,
      ],
    },
    {
      heading: '7. Your Choices and Rights',
      body: [
        'Depending on where you live, you may have the right to:',
        [
          'access the personal data we hold about you;',
          'correct inaccurate data;',
          'request deletion of your data, where the law allows it;',
          'object to or restrict certain processing.',
        ],
        'To exercise any of these, contact us at [contact address to be confirmed]. We will respond ' +
          'within the time required by applicable law.',
      ],
    },
    {
      heading: '8. Cookies and Similar Technologies',
      body: [
        `We use a minimal set of cookies: one to keep you signed in, and one to remember basic ` +
          `preferences. We do not use third-party advertising or cross-site tracking cookies.`,
      ],
    },
    {
      heading: '9. Children’s Privacy',
      body: [
        `The Service is intended for business use and is not directed at children. We do not ` +
          `knowingly collect personal data from anyone under 16.`,
      ],
    },
    {
      heading: '10. Changes to This Policy',
      body: [
        `We may update this Policy as the Service or our practices change. We will post the ` +
          `updated Policy here and update the effective date; for material changes we will make a ` +
          `reasonable effort to let you know directly.`,
      ],
    },
    {
      heading: '11. Contact Us',
      body: [`Questions about this Policy can be sent to [contact address to be confirmed].`],
    },
  ],
};
